import './Redocs.scss';

import { Loading } from '@whistler-ui/k2/components/Loading';
import { cn } from '@whistler-ui/k2/helpers/classBuilders';
import { useClassHelper } from '@whistler-ui/k2/hooks/useClassHelper';
import { lazy, Suspense } from 'react';

import { useThemeStore } from '../../store/theme';

const componentName = 'c-Redocs';

/**
 * `redoc` pulls mobx and styled-components behind it and is only ever needed by
 * the two API-reference tabs. Imported statically it reached the entry chunk
 * through the `components` barrel, so every page of the portal — the login
 * screen included — paid for it. Loading it here keeps it in its own chunk,
 * fetched the first time a spec actually renders.
 */
const RedocStandalone = lazy(() => {
  return import('redoc').then(module => {
    return { default: module.RedocStandalone };
  });
});

const RedocLoading = (
  <div className="min-h-[500px] flex items-center justify-center">
    <Loading />
  </div>
);

export function Redocs({ isLoading, spec, onLoaded, fallback }) {
  const c = useClassHelper(componentName);
  const { colors } = useThemeStore();

  return (
    <div
      className={`${c('base')} ${cn({
        'rounded-[var(--border-radius-base)] overflow-hidden relative z-0':
          spec,
      })}`}
    >
      {isLoading && RedocLoading}
      {spec && (
        <div
          className={cn('hidden', {
            block: !isLoading,
          })}
        >
          <div className="no-tw-styles">
            <Suspense fallback={RedocLoading}>
              <RedocStandalone
                onLoaded={onLoaded}
                specUrl={spec}
                options={{
                  hideLoading: true,
                  nativeScrollbars: true,
                  // Account for the sticky top-nav (~75 px) + tabs bar (~55 px) so
                  // that Redoc scrolls operation sections below the fixed headers and
                  // its IntersectionObserver correctly tracks the active hash.
                  scrollYOffset: 130,
                  theme: { colors: { primary: { main: colors.informative } } },
                }}
              />
            </Suspense>
          </div>
        </div>
      )}
      {!isLoading && !spec && fallback}
    </div>
  );
}

export default Redocs;
